<template>
  <div
    class="nx-searcher white"
    :class="{ 'searcher-is-selectable': selectable }"
  >
    <v-layout
      class="layout-search pt-4"
      :class="{'pb-5': !entityTabs}"
    >
      <v-flex class="searcher-sidebar hidden-sm-and-down" />
      <v-flex class="searcher-main-container">
        <v-flex class="searcher-main">
          <v-toolbar
            tabs
            flat
            color="transparent"
          >
            <v-text-field
              prepend-inner-icon="search"
              :label="$t('message.searcher.label')"
              solo
              :value="text"
              autofocus
              @keyup="searchByText"
            >
              <template v-slot:append>
                <nx-searcher-saved-searches
                  :filters="filters"
                  :sort="sort"
                  :entity="entity"
                  :show-only-current-entity="selectable"
                  @load-saved-search="loadSavedSearch"
                />
              </template>
            </v-text-field>
            <nx-tabs
              v-if="showTabs"
              slot="extension"
              :tabs="tabs"
              :value="currentTab"
              color="transparent"
              slider-color="black"
              @input="changeEntity"
            />
          </v-toolbar>
        </v-flex>
      </v-flex>
      <v-flex
        v-if="selectable"
        class="searcher-sidebar hidden-sm-and-down"
      />
    </v-layout>

    <v-layout class="layout-results">
      <nx-searcher-filters
        v-if="hasAvailableFilters"
        class="searcher-sidebar"
        :entity="entity"
        :available-filters="availableFilters"
        :disabled-filters="disabledFilters"
        :value="filters"
        @input="updateFilters"
      />
      <v-flex class="searcher-main-container searcher-main-container-list">
        <v-flex class="searcher-main searcher">
          <nx-searcher-list
            class="mt-3"
            :entity="entity"
            :total-items="numResults"
            :items="formattedItems"
            :sort-filters="sortOptions"
            :sort="sort"
            :actions="entityActions"
            :global-actions="entityGlobalActions"
            :selectable="selectable"
            :view="listView"
            :bulk-selection="hasBulkSelection"
            :multiple-selection="hasMultipleSelection"
            :filters="filters"
            :loading="loading"
            :selection="selectedItems"
            :search-limit="searchLimit"
            :search-limit-options="searchLimitOptions"
            :search-results-have-changed="searchResultsHaveChanged"
            :search-results-diff="newResultsDiff"
            @click:item="onSelectedEntity"
            @click:refresh="refresh"
            @input:add-filter="mergeFilters"
            @input:search-limit="setSearchLimit"
            @input:set-filter="setFilters"
            @input:sort-filters="updateSort"
            @input:view="listView = $event"
            @refresh="refresh"
            @refreshItem="refreshItem"
            @update:item="updateItem"
          />
          <v-btn
            v-if="hasMorePages || (items.length > 0 && loading)"
            class="d-flex mx-auto"
            color="secondary"
            :loading="loading"
            @click="loadMore"
          >
            {{ $t('message.common.loadMore') }}
          </v-btn>
        </v-flex>
      </v-flex>
      <v-flex
        v-if="selectable"
        class="searcher-sidebar"
      >
        <nx-searcher-selection
          :value="selectedItems"
          :sortable="hasMultipleSelection"
          @click:remove="removeSelectedItem"
          @change="onSelectionChange"
        />
      </v-flex>
    </v-layout>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import {
  debounce, get, isEmpty, isObject,
} from 'lodash';
import NxSearcherSavedSearches from '@/modules/searcher/saved-searches/SavedSearches';
import NxTabs from '@/components/Tabs';
import NxSearcherSelection from '@/modules/searcher/selection/Selection';
import NxSearcherFilters from '@/modules/searcher/filters/SearcherFilters';
import { makeFormatter } from '@/modules/list/config';
import { getSearcherFilterWatchFunctions, LIST_VIEW_NORMAL } from '@/modules/searcher/config';
import searchableWithPolling from '@/modules/searcher/mixins/searchable-with-polling';
import NxSearcherList from './SearchList';

/**
 * @typedef {{}} SearcherResult
 * @property {number} entityId - The entity identifier
 * @property {string} entityType - The entity type (content, taxonomy....)
 * @property {Object} entity - The entity card representation (ContentCard, TaxonomyCard etc...)
 */

/**
 * Entity searcher
 *
 * @property {String} entity - Entity being searched for.
 * @property {Boolean|string[]} [entityTabs] - true/false to indicate whether to show the entity tabs, allowing the user
 *                              to change between entities or not, or an array indicating which tabs will be displayed.
 * @property {{}}} filters - Search filters
 * @property {Number} [limit] - Maximum number of entities that can be selected.
 * @property {String} [text] - Text searched for.
 * @property {Array} [value] - Array with the selected entities.
 *
 * @fires Searcher#input - Emits changes made in the selection.
 * @fires Searcher#changeEntity - Emits the new entity selected by the user.
 * @fires Searchable#setFilters
 * @fires Searchable#updateSort
 * @return {SearcherResult[]} value Array of SearcherResults
 */
export default {
  name: 'NxSearcher',
  components: {
    NxTabs,
    NxSearcherList,
    NxSearcherFilters,
    NxSearcherSelection,
    NxSearcherSavedSearches,
  },
  mixins: [
    searchableWithPolling,
  ],
  props: {
    bulkSelection: {
      type: Boolean,
    },
    disableBulkActions: {
      type: Boolean,
      default: false,
    },
    disabledFilters: {
      type: Array,
      default: () => ([]),
    },
    entity: {
      type: String,
      required: true,
    },
    entityTabs: {
      type: [Array, Boolean],
      default: true,
    },
    filters: {
      type: Object,
      required: true,
    },
    filtersParams: {
      type: Object,
      default: () => ({}),
    },
    limit: {
      type: Number,
      default: null,
    },
    preview: {
      type: Boolean,
    },
    selectable: {
      type: Boolean,
    },
    sort: {
      type: Object,
      required: true,
    },
    text: {
      type: String,
      default: '',
    },
    // Selected entities
    value: {
      type: Array,
      default() {
        return [];
      },
    },
  },
  data() {
    return {
      listView: LIST_VIEW_NORMAL,
      previousSort: null,
    };
  },
  methods: {
    removeSelectedItem(id) {
      this.$emit('input', this.value.filter(e => e.entityId !== id));
    },
    loadSavedSearch({ entity, filters, sort }) {
      console.debug('load-saved-search', entity, filters, sort);
      this.changeEntity(entity);
      this.setFilters(filters);
      if (sort) {
        this.updateSort(sort);
      }
    },
    changeEntity(entity) {
      if (this.entity !== entity) {
        this.items = [];
      }
      /** @event Searcher#changeEntity {String} */
      this.$emit('changeEntity', entity);
    },
    /** @event Searcher#input {SearcherResult[]} */
    onSelectedEntity(entity) {
      const entityItem = this.filteredItems.find(item => item.id === entity.id);
      const formattedEntity = this.formatEntity(entityItem);
      if (this.limit === 1) {
        this.$emit('input', [formattedEntity]);
      } else if (!this.limitReached) {
        const selectedEntity = this.value.find(e => e.entityId === entity.id);
        if (selectedEntity) {
          this.$emit('input', this.value.filter(e => e.entityId !== selectedEntity.entityId));
          return;
        }
        this.$emit('input', this.value.concat(formattedEntity));
      }
    },
    /** @event Searcher#input {SearcherResult[]} */
    onSelectionChange(selection) {
      this.$emit('input', selection.map(this.formatEntity));
    },
    // eslint-disable-next-line
    searchByText: debounce(function (event) {
      const searchText = isObject(event) ? event.target.value : event;
      let sort;

      if (searchText) {
        // Si han añadido el filtro textual y existe un criterio de ordenación predeterminado para
        // la búsqueda textual, nos gurdamos el criterio actual y establecemos el predeterminado.
        if (isEmpty(this.filters.text) && this.defaultTextualSortOption) {
          this.previousSort = this.sort;
          sort = this.defaultTextualSortOption;
        }
      } else if (!isEmpty(this.filters.text) && this.previousSort) {
        // Por el contrario, si habíamos cambiado el criterio de búsqueda al añadir el filtro
        // textual y ahora lo han borrado, restablecemos el criterio anterior.
        sort = this.previousSort;
        this.previousSort = null;
      }

      const filters = {
        ...this.filters,
        text: searchText.trim() ? searchText : undefined,
      };
      this.$emit('setFilters', filters, sort);
    }, 400),
    updateItem(item) {
      const index = this.items.findIndex(i => i.entityId === item.id);
      this.$set(this.items, index, { ...this.items[index], entity: item });
    },
    processFilterChanges(newFilters, oldFilters) {
      // Miramos en las funciones configurables por hay que invocar alguna de ellas para modificar el comportamiento del searcher en base a los filtros.
      getSearcherFilterWatchFunctions(this.entity)
        .forEach(func => func.call(this, newFilters, oldFilters));
    },
  },
  computed: {
    ...mapGetters('searcher', [
      'actions',
      'globalActions',
    ]),
    ...mapGetters(['searcher', 'searcherTabs']),
    entityActions() {
      if (this.disableBulkActions) {
        return [];
      }
      return this.actions(this.entity);
    },
    entityGlobalActions() {
      if (this.disableBulkActions) {
        return [];
      }
      return this.globalActions(this.entity);
    },
    hasBulkSelection() {
      return this.bulkSelection || this.entityActions.length > 0;
    },
    hasMultipleSelection() {
      return this.hasBulkSelection || (this.selectable && this.limit !== 1);
    },
    title() {
      if (this.loading) return this.$t('message.searcher.loadingResults');
      const num = this.numResults.toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      return `${num} ${this.$tc('message.searcher.results', this.numResults)}`;
    },
    formattedItems() {
      // https://github.com/vuejs/vue/issues/2000#issuecomment-527146280
      return Object.freeze(
        this.filteredItems.map(item => ({
          siteId: item.siteId,
          ...(item.list ? item.list : makeFormatter(this.entity)(item)),
        })),
      );
    },
    selectedItems() {
      return this.value
        .map(item => item.entity)
        .map(entity => (isObject(entity.list) ? entity.list : makeFormatter(this.entity)(entity)));
    },
    filteredItems() {
      return this.items
        .map(item => item.entity);
    },
    entityConfig() {
      // All hasn't config
      if (this.entity === 'all') {
        return {};
      }
      const entityConfig = this.searcher[this.entity];
      if (!entityConfig) {
        console.error('Entity config not present in configuration');
        return {};
      }
      return entityConfig;
    },
    sortOptions() {
      if (!this.entityConfig.sort) {
        return null;
      }
      return this.entityConfig.sort.available;
    },
    defaultTextualSortOption() {
      return get(this.entityConfig, 'sort.defaultTextual', null);
    },
    showTabs() {
      if (this.entityTabs === false) {
        return false;
      }
      if (!this.searcherTabs(this.entity)) {
        return false;
      }
      return true;
    },
    availableFilters() {
      // All hasn't filters
      if (this.entity === 'all') {
        return [];
      }
      if (!this.entityConfig.filters) {
        console.error('Entity filters not present in configuration');
        return [];
      }
      return this.entityConfig.filters.map((item) => {
        if (this.filtersParams[item.filter]) {
          return {
            ...item,
            params: { ...item.params, ...this.filtersParams[item.filter] },
          };
        }
        return item;
      });
    },
    hasAvailableFilters() {
      return this.availableFilters.length > 0;
    },
    currentTab() {
      return this.entity;
    },
    limitReached() {
      return this.limit && this.value.length >= this.limit;
    },
    tabs() {
      const tabs = Array.isArray(this.entityTabs)
        ? this.entityTabs
        : this.searcherTabs(this.entity);

      return tabs.map(tab => ({
        id: tab,
        label: this.$tc(`message.searcher.tab.${tab}`),
      }));
    },
    // Override to add tie-breaker sorting options.
    searchFilters() {
      const searchFilters = {
        entity: this.entity,
        filters: {
          ...this.filters,
          page: this.page,
          limit: this.searchLimit,
        },
      };

      if (this.sort) {
        const sort = [this.sort];
        // In content searcher, add modification date as sorting tiebreaker.
        if (this.entity === 'content') {
          const sortingByField = Object.keys(this.sort)[0];
          if (sortingByField !== undefined
            && sortingByField !== 'modificationDate'
          ) {
            sort.push({ modificationDate: 'DESC' });
          }
        }
        searchFilters.filters.sort = sort;
      }

      return searchFilters;
    },
  },
  watch: {
    filters: {
      async handler(value, oldValue) {
        this.processFilterChanges(value, oldValue);
      },
    },
  },
  mounted() {
    this.processFilterChanges(this.filters, {});
  },
};
</script>

<style lang="stylus">
@import '../../assets/stylus/variables.styl'

$gap = 116px;
$reducedGap = 66px;
$sidebarWidth = 280px;
$mainMaxWidth = 1200px;

.nx-searcher {
  width: 100%
  height: 100%

  .layout-search {
    box-shadow: inset 0 -1px 0 0 $nx-primary--lighten-2;

    .v-toolbar__content, .v-toolbar__extension {
      padding: 0;
    }
  }

  .layout-results {
    height: auto;
    min-height: 100%;
    flex-direction: row;
    @media $sm-and-down {
      flex-direction: column;
    }
  }

  .searcher-sidebar {
    flex: 0 0 $sidebarWidth;
    max-width: 400px;
    @media $sm-and-down {
      max-width: 100%;
      flex: 0 0 auto;
    }
  }

  .searcher-main-container {
    padding: 0 $gap;
    flex: 1 1 auto;

    .nx-view--bag-visible &,
    .searcher-is-selectable& {
      padding: 0 $reducedGap;
    }
    @media $sm-and-down {
      padding: 0 20px;
    }
    @media $xs-only {
      padding: 0 15px;
    }

    &-list {
      background-color: $nx-primary--lighten-5;

      // Flex items have `min-width: auto` by default, causing them to overflow their parents.
      // @see https://defensivecss.dev/tip/flexbox-min-content-size/
      min-width: 0;
    }
  }

  .searcher-main {
    margin: 0 auto;
    max-width: $mainMaxWidth;
  }
}
</style>
